import { useQuery } from '@tanstack/react-query'

import { createApiCall } from 'services'
import type { CertificateAdmin } from 'types'

type UseCertificateParams = {
  certificateId: string
}

type UseCertificateOptions = {
  enabled?: boolean
}

/**
 * VRG-528 — the platform's certificate DETAIL payload.
 *
 * ⚠️ This used to call `GET /certificates/public/:id`, and that was the source of a
 * whole family of "the detail page disagrees with the list" bugs. That endpoint is
 * `@OptionalAuth()`: it serves the FULL payload only to the certificate's consumer
 * OWNER, so a brand admin opening a certificate their own company issued received
 * `toCertificateShortInfo` — the ANONYMOUS-SCANNER projection — with `customer` then
 * nulled unless the buyer had switched their `show_ownership` view toggle on.
 *
 * The cast below is what hid it. The response was cast to `CertificateAdmin`, whose
 * `customer`/`company` members are non-optional, so `certificate.customer` COMPILED
 * while being `undefined` at runtime: any consumer reading it typechecked green and
 * rendered an em-dash over a certificate that demonstrably had an owner. A false
 * statement, not an absence.
 *
 * `GET /certificates/admin/:id` is `@RequireAdmin()` + `ViewCertificates` +
 * company-scoped, and returns the SAME `toCertificateFullInfo` projection the
 * certificates TABLE renders — one server-side computation, so the header, the info
 * block and the row cannot disagree. The cast is now honest.
 *
 * This hook is platform-only; `apps/app` does not import it, so the consumer app's
 * public path is untouched.
 */
type UseCertificateResult = {
  data: CertificateAdmin | null
  isLoading: boolean
  isError: boolean
  error: unknown
  refetch: () => Promise<void>
  isRefetching: boolean
}

export const useCertificate = (
  params: UseCertificateParams,
  options?: UseCertificateOptions
): UseCertificateResult => {
  const query = useQuery({
    queryKey: ['certificates', 'detail', params.certificateId],
    enabled: options?.enabled !== false && Boolean(params?.certificateId),
    queryFn: async () => {
      const response = await createApiCall({
        method: 'GET',
        url: `/certificates/admin/${params.certificateId}`,
      })

      if (response.status !== 200) {
        throw new Error(`Failed to fetch certificate ${params.certificateId}`)
      }

      return response.data as CertificateAdmin
    },
  })

  return {
    data: query.data ?? null,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    isRefetching: query.isRefetching,
    refetch: async () => {
      await query.refetch()
    },
  }
}
